import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { useNavigate, Link } from "react-router-dom";
import { ArrowLeft, Save } from "lucide-react";
import { handleSend, handleGet, handleUpdate } from "../../../controllers/controller.all";
import { toast } from "react-toastify";

export default function AffectTeacher() {
    const user = useSelector((state) => state.userReducer?.user);
    const token = user?.access_token || "";
    const ecoleId = user?.ecole?._id || user?.ecole;
    const navigate = useNavigate();

    const [loading, setLoading] = useState(false);
    const [profs, setProfs] = useState([]);
    const [classes, setClasses] = useState([]);
    const [matieres, setMatieres] = useState([]);
    const [existingAssignments, setExistingAssignments] = useState([]);
    
    const [formData, setFormData] = useState({
        prof: "",
        classe: "",
        matieres: []
    });

    useEffect(() => {
        const fetchData = async () => {
            // Fetch profs
            await handleGet(`/user/s/all/professeurs/${ecoleId}`, (userRes) => {
                if (userRes?.users) {
                    setProfs(userRes.users);
                }
            }, token);

            // Fetch classes
            await handleGet(`/classe/ecole/${ecoleId}`, (classeRes) => {
                if (classeRes?.classes) {
                    setClasses(classeRes.classes.filter(c => c.ecole === ecoleId || c.ecole?._id === ecoleId));
                }
            }, token);

            // Fetch matieres
            await handleGet(`/matiere/ecole/${ecoleId}`, (matiereRes) => {
                if (matiereRes?.matieres) {
                    setMatieres(matiereRes.matieres.filter(m => m.ecole === ecoleId || m.ecole?._id === ecoleId));
                }
            }, token);
        };
        if(token) fetchData();
    }, [token, ecoleId]);

    useEffect(() => {
        if (formData.prof) {
            handleGet(`/prof_classe/prof/${formData.prof}`, (res) => {
                if (res?.prof_classes) {
                    setExistingAssignments(res.prof_classes);
                }
            }, token);
        } else {
            setExistingAssignments([]);
        }
    }, [formData.prof, token]);

    useEffect(() => {
        if (formData.prof && formData.classe && existingAssignments.length > 0) {
            const existingProfClasse = existingAssignments.find(a => 
                (a.classe?._id === formData.classe || a.classe === formData.classe)
            );
            if (existingProfClasse && existingProfClasse.matieres) {
                setFormData(prev => ({
                    ...prev,
                    matieres: existingProfClasse.matieres.map(m => m._id || m)
                }));
            } else {
                setFormData(prev => ({ ...prev, matieres: [] }));
            }
        } else if (!formData.classe) {
            setFormData(prev => ({ ...prev, matieres: [] }));
        }
    }, [formData.prof, formData.classe, existingAssignments]);

    const handleSelectMatiere = (e) => {
        const val = e.target.value;
        if(val && !formData.matieres.includes(val)) {
            setFormData({ ...formData, matieres: [...formData.matieres, val] });
        }
    };
    
    const removeMatiere = (mId) => {
        setFormData({ ...formData, matieres: formData.matieres.filter(id => id !== mId) });
    };

    const handleSubmit = async (e) => {
        if(formData.matieres.length === 0) {
            e.preventDefault();
            toast.warn("Veuillez sélectionner au moins une matière.");
            return;
        }

        const existingProfClasse = existingAssignments.find(a => 
            (a.classe?._id === formData.classe || a.classe === formData.classe)
        );

        if (existingProfClasse) {
            e.preventDefault();
            const payload = {
                prof: existingProfClasse.prof?._id || existingProfClasse.prof,
                classe: existingProfClasse.classe?._id || existingProfClasse.classe,
                matieres: formData.matieres,
                ecole: ecoleId
            };
            
            const res = await handleUpdate(null, `/prof_classe/${existingProfClasse._id}`, payload, setLoading, token);
            if (res) {
                navigate("/teachers");
            }
        } else {
            const payload = {
                ...formData,
                ecole: ecoleId
            };
            
            const res = await handleSend(e, "/prof_classe", payload, setLoading, token);
            if (res) {
                navigate("/teachers");
            }
        }
    };

    return (
        <div className="flex flex-col gap-6 max-w-2xl mx-auto">
            <div className="flex items-center gap-4">
                <Link to="/teachers" className="p-2 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition text-gray-500">
                    <ArrowLeft className="h-5 w-5" />
                </Link>
                <div>
                    <h1 className="text-2xl font-bold text-gray-900 tracking-tight">Affecter un Professeur</h1>
                    <p className="text-sm text-gray-500">Attribuer des classes et matières à un professeur existant.</p>
                </div>
            </div>

            <form onSubmit={handleSubmit} className="bg-white shadow-sm border border-gray-200 rounded-xl overflow-hidden">
                <div className="p-6 space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                            <label className="text-sm font-medium text-gray-700">Professeur *</label>
                            <select required value={formData.prof} onChange={e => setFormData({...formData, prof: e.target.value})} className="w-full border border-gray-300 rounded-lg py-2 px-3 focus:outline-none focus:ring-2 focus:ring-indigo-500">
                                <option value="">Sélectionner</option>
                                {profs.map(p => <option key={p._id} value={p._id}>{p.prenom} {p.nom}</option>)}
                            </select>
                        </div>
                        <div className="space-y-2">
                            <label className="text-sm font-medium text-gray-700">Classe *</label>
                            <select required value={formData.classe} onChange={e => setFormData({...formData, classe: e.target.value})} className="w-full border border-gray-300 rounded-lg py-2 px-3 focus:outline-none focus:ring-2 focus:ring-indigo-500">
                                <option value="">Sélectionner</option>
                                {classes.map(c => <option key={c._id} value={c._id}>{c.libelle}</option>)}
                            </select>
                        </div>
                    </div>

                    <div className="space-y-2 mt-4">
                        <label className="text-sm font-medium text-gray-700">Matières enseignées *</label>
                        <select onChange={handleSelectMatiere} value="" className="w-full border border-gray-300 rounded-lg py-2 px-3 focus:outline-none focus:ring-2 focus:ring-indigo-500">
                            <option value="">Ajouter une matière...</option>
                            {matieres.map(m => !formData.matieres.includes(m._id) && <option key={m._id} value={m._id}>{m.libelle}</option>)}
                        </select>
                        
                        <div className="flex flex-wrap gap-2 mt-3">
                            {formData.matieres.map(mId => {
                                const mat = matieres.find(m => m._id === mId);
                                return (
                                    <div key={mId} className="flex items-center gap-2 bg-indigo-50 text-indigo-700 px-3 py-1.5 rounded-full text-sm font-medium">
                                        {mat?.libelle}
                                        <button type="button" onClick={() => removeMatiere(mId)} className="text-indigo-400 hover:text-indigo-900 font-bold ml-1">×</button>
                                    </div>
                                )
                            })}
                            {formData.matieres.length === 0 && <span className="text-sm text-gray-400">Aucune matière sélectionnée</span>}
                        </div>
                    </div>
                </div>
                <div className="bg-gray-50 px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
                    <Link to="/teachers" className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition">
                        Annuler
                    </Link>
                    <button type="submit" disabled={loading} className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 transition disabled:opacity-50">
                        <Save className="h-4 w-4" /> Enregistrer
                    </button>
                </div>
            </form>
        </div>
    );
}
